import { useState } from 'react';
import { array, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { WithGuideLink } from '@mews-commander/types';
import { LocalizedPageTitle, OptimusProvider, PortfolioLayout, PortfolioLayoutHeader } from '@mews-commander/ui';
import { TextKey, RouteName } from '@mews-commander/enums';
import {
    Link,
    Table,
    TableActiveColumnFilters,
    ActionToolbar,
    TableHeader,
    TableProvider,
    useFullRowSelection,
} from '@optimus-web/core';
import { LocalizedText, useUrlSyncedTableState } from '@mews/framework';
import { PortfolioEnterpriseGroupResult, PortfolioEnterpriseGroupFilters } from '@mews-commander/data-access';
import { useResolveRoute } from '@mews/routing';
import { CreateRateDialog } from '../../../enterpriseRateManagement/components/sections/components';
import { usePropertiesGroupsData } from '../../queries/index';
import { PropertyGroupsDeleteWrapper } from '../PropertyGroupsDeleteWrapper';
import { PropertyGroupsDetails } from '../PropertyGroupsDetails/PropertyGroupsDetails';
import { TableHeaderRowActions } from '../../components/RowActions/TableHeaderRowActions';
import { CreateRateGroupDialog } from '../../../enterpriseRateManagement/components/sections/components/rateGroup/CreateRateGroupDialog';
import { getColumns } from './tableMapping';

export const PropertyGroupsTable = ({ guideLink }: WithGuideLink) => {
    const [selectedPropertyGroup, setSelectedPropertyGroup] = useState<PortfolioEnterpriseGroupResult>();
    const resolveRoute = useResolveRoute();

    const columns = getColumns({
        onDelete: (propertyGroupId: string) =>
            setRowSelection(({ [propertyGroupId]: removed, ...otherSelection }) => otherSelection),
        onNameClick: (propertyGroup: PortfolioEnterpriseGroupResult) => setSelectedPropertyGroup(propertyGroup),
    });

    const [tableState, setTableState] = useUrlSyncedTableState<
        PortfolioEnterpriseGroupResult,
        PortfolioEnterpriseGroupFilters
    >({
        columns,
    });

    const { data, isLoading } = usePropertiesGroupsData(tableState);

    const { selectedRowsData, rowSelection, setRowSelection, selectedRowsIds, hasRowsSelected, resetRowSelection } =
        useFullRowSelection(data?.data);

    const propertyGroupsEnterprises = pipe(
        selectedRowsData,
        array.map((row) => row.propertyIds),
        array.flatten,
        array.uniq(string.Eq)
    );

    return (
        <OptimusProvider density="spacious">
            <PortfolioLayout>
                <PortfolioLayoutHeader
                    title={<LocalizedPageTitle textKey={TextKey.PropertyGroupsNavigation} />}
                    guideLink={guideLink}
                />
                <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
                    <TableHeader
                        title={<LocalizedText textKey={TextKey.PropertyGroupsTableHeader} />}
                        subTitle={data?.totalRowCount || '0'}
                    />

                    <TableActiveColumnFilters />

                    <ActionToolbar
                        disabled={!hasRowsSelected}
                        leftSide={
                            array.isNonEmpty(selectedRowsIds) && (
                                <LocalizedText
                                    textKey={TextKey.SelectedItems}
                                    substitutes={{ count: selectedRowsIds.length }}
                                />
                            )
                        }
                        rightSide={
                            <>
                                <CreateRateDialog enterpriseIds={propertyGroupsEnterprises} />

                                <CreateRateGroupDialog
                                    propertyGroupsIds={selectedRowsIds}
                                    enterpriseIds={propertyGroupsEnterprises}
                                />

                                <PropertyGroupsDeleteWrapper
                                    selectedPropertyGroups={selectedRowsData}
                                    onSuccess={resetRowSelection}
                                />

                                <TableHeaderRowActions enterpriseIds={propertyGroupsEnterprises} />
                            </>
                        }
                    />

                    <Table
                        data={data?.data ?? []}
                        amountOfRows={data?.totalRowCount ?? 0}
                        loading={isLoading}
                        rowSelection={{ rowSelection, onRowSelectionChange: setRowSelection }}
                        emptyState={{
                            primaryText: <LocalizedText textKey={TextKey.PropertyGroupsEmptyStateTitle} />,
                            secondaryText: <LocalizedText textKey={TextKey.PropertyGroupsEmptyStateDescription} />,
                            action: (
                                <Link href={resolveRoute(RouteName.EnterpriseRelationshipReportIndex)} standalone>
                                    <LocalizedText textKey={TextKey.PropertyGroupsEmptyStateLink} />
                                </Link>
                            ),
                        }}
                        uniqueRowId="id"
                        fixedFirstColumn
                        fixedLastColumn
                    />
                </TableProvider>

                <PropertyGroupsDetails
                    propertyGroup={data?.data.find((propertyGroup) => propertyGroup.id === selectedPropertyGroup?.id)}
                    isOpened={!!selectedPropertyGroup}
                    onUnmount={() => setSelectedPropertyGroup(undefined)}
                />
            </PortfolioLayout>
        </OptimusProvider>
    );
};
